import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Container, Stack } from '@mui/material';
import { darken } from '@mui/material/styles';
import { CalendarPlus, ListPlus } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import EventDetails from '../components/events/EventDetails';
import { LoadingIndicator } from '../components/assets';
import {
  addPublicEventToDopatize,
  getAuthStatus,
  getPublicEvent,
  getPublicEventCalendarFile,
} from '../utils/api';

const actionSx = (colorKey) => (theme) => {
  const color = theme.palette[colorKey].main;
  return {
    m: 0,
    color: theme.palette.background.default,
    bgcolor: color,
    borderColor: color,
    boxShadow: `0 6px 0 ${darken(color, 0.58)}`,
    '&:hover': { color: theme.palette.background.default, bgcolor: color, transform: 'translate(-2px, -2px)' },
    '&:active': { transform: 'translateY(4px)', boxShadow: 'none' },
  };
};

export default function PublicEvent() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoAddStarted = useRef(false);
  const [event, setEvent] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    Promise.all([
      getPublicEvent(token),
      getAuthStatus().catch(() => ({ authenticated: false })),
    ])
      .then(([loadedEvent, auth]) => {
        if (!alive) return;
        setEvent(loadedEvent);
        setAuthenticated(Boolean(auth?.authenticated));
      })
      .catch((loadError) => {
        if (alive) setError(loadError.data?.message || loadError.message || 'This event link is not available.');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const addToDopatize = useCallback(async () => {
    if (!authenticated) {
      const nextPath = `/public/event/${encodeURIComponent(token)}?add=1`;
      navigate(`/sign-up?invite=${encodeURIComponent(token)}&next=${encodeURIComponent(nextPath)}`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await addPublicEventToDopatize(token);
      navigate('/events');
    } catch (saveError) {
      setError(saveError.data?.message || saveError.message || 'Unable to add this event.');
    } finally {
      setSaving(false);
    }
  }, [authenticated, navigate, token]);

  useEffect(() => {
    if (
      loading
      || !event
      || !authenticated
      || searchParams.get('add') !== '1'
      || autoAddStarted.current
    ) return;
    autoAddStarted.current = true;
    addToDopatize();
  }, [addToDopatize, authenticated, event, loading, searchParams]);

  const addToCalendar = async () => {
    setDownloading(true);
    setError('');
    try {
      const { blob, filename } = await getPublicEventCalendarFile(token);
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (downloadError) {
      setError(downloadError.data?.message || downloadError.message || 'Unable to download this calendar event.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 3, pb: 18 }}>
      {loading ? <LoadingIndicator text="loading event" /> : (
        <Stack spacing={2.5}>
          {error && <Alert severity="error" sx={{ color: 'secondary.main' }}>{error}</Alert>}
          {event && (
            <>
              <EventDetails event={event} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<ListPlus size={18} />}
                  disabled={saving}
                  onClick={addToDopatize}
                  sx={actionSx('primary')}
                >
                  {saving ? 'Adding...' : 'Add to Dopatize'}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CalendarPlus size={18} />}
                  disabled={downloading}
                  onClick={addToCalendar}
                  sx={actionSx('green')}
                >
                  {downloading ? 'Downloading...' : 'Add to calendar'}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      )}
    </Container>
  );
}
